import React, { useContext, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import useGetRequest from 'hooks/useGetRequest';
import { AuthContext } from 'contextProviders/AuthContext';
import { CourseSchedule } from 'models/CourseSchedule';
import { Activity } from 'models/Activity';
import EnrolledUserTable from 'pages/trainer/scheduledCourses/components/EnrolledUserTable';
import { ScheduledLiveTrainingsTable } from 'pages/schedule/courses/components/ScheduledLiveTrainingsTable';
import QuestionnaireResults from 'pages/trainer/scheduledCourses/components/QuestionnaireResults';
import { MainSectionWithGaps } from 'components/sections/MainSection';
import ShareAndCancelCourse from './ShareAndCancelCourse';
import { RoleType } from 'models/User';
import strings from '../../strings/strings.json';
import { logError } from '../../lib/debug-helpers';
import Notification, { NotificationType } from '../notifiers/Notification';
import TabStructureComponent from 'components/tabs/TabStructureComponent';
import VideoCallIcon from 'assets/icons/controls/VideoCallIcon';
import { TabDefinition } from 'components/tabs/Tabs';
import LearnerIcon from 'assets/icons/role-icons/LearnerIcon';
import QuestionnaireIcon from 'assets/icons/pathway/QuestionnaireIcon';
import { styled } from 'styled-components';
import { breakpoints, FONTSIZE, sizes } from 'theme';
import ScheduleIcon from 'assets/icons/pathway/ScheduleIcon';
import { BodyLarge, FlexAlignCenter, FlexCol } from 'components/Elements';
import WarningIcon from 'assets/icons/indicators/WarningIcon';
import { ErrorMessage } from 'components/forms/FormComponents';
import NavBackButton from 'components/buttons/NavBackButton';
import { LearnerCardBase } from 'pages/learner/sharedStyles/LearnerStyles';
import { IconSize } from 'assets/icons/icon-sizes';
import Header from 'components/sections/Header';

type BackButtonDetails = {
    title: string;
    aria: string;
    href: string;
    navigateState?: any;
    functionOverride?: Function;
};

type LiveCourseDetailsProps = {
    backButtonDetails: BackButtonDetails;
};

type State = {
    activeTabId: string;
};

export default function LiveCourseDetails({ backButtonDetails }: LiveCourseDetailsProps): JSX.Element {
    const { courseId } = useParams();
    const { state } = useLocation();
    const { activeTabId } = (state as State) ?? {};

    const {
        data: courseSchedule,
        loading: loadingCourse,
        errors: courseErrors,
    } = useGetRequest<CourseSchedule>(`courses/schedule/${courseId}`);

    const closeEnrolLinkModalButtonRef = useRef<HTMLButtonElement>(null);
    const cancelCourseButtonRef = useRef<HTMLButtonElement>(null);

    const { userData } = useContext(AuthContext);
    const userIsOrgAdmin = userData.activeRole === RoleType.ORGANISATION_ADMIN;

    if (!courseSchedule || loadingCourse) return <></>;

    if (courseErrors.length > 0) {
        logError(courseErrors);

        return (
            <MainSectionWithGaps>
                <Notification
                    notificationType={NotificationType.DANGER}
                    title={strings.scheduledCourseDetailsPage.loadFailed}
                    description={strings.errorMessages.genericDetail}
                    big
                />
            </MainSectionWithGaps>
        );
    }

    const tabDefinitions: TabDefinition[] = [];

    courseSchedule.meetings.length > 0 &&
        tabDefinitions.push({
            id: 'live-training',
            name: 'Live training',
            icon: VideoCallIcon,
            content: <ScheduledLiveTrainingsTable courseSchedule={courseSchedule} />,
        });

    tabDefinitions.push({
        id: 'learners',
        name: 'Learners',
        icon: LearnerIcon,
        content: (
            <EnrolledUserTable courseScheduleId={courseSchedule.id} maximumLearners={courseSchedule.maximumLearners} />
        ),
    });

    const questionnaires: Activity[] =
        courseSchedule.course.modules.flatMap((module) =>
            module.activities.filter((x) => x.type === 'QUESTIONNAIRE'),
        ) ?? [];

    questionnaires.length > 0 &&
        tabDefinitions.push({
            id: 'questionnaires',
            name: 'Questionnaires',
            icon: QuestionnaireIcon,
            content: <QuestionnaireResults questionnaires={questionnaires} />,
        });

    const courseStartDate = new Date(courseSchedule.startDate);

    const endOfYesterday = new Date();
    endOfYesterday.setDate(endOfYesterday.getDate() - 1);
    endOfYesterday.setHours(23, 59, 59, 59);

    const courseHasEnded = new Date(courseSchedule.endDate) <= endOfYesterday;

    const formattedStartDate = courseStartDate.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
    });

    let formattedEndDate;

    if (courseSchedule.endDate) {
        let courseEndDate = new Date(courseSchedule.endDate);
        formattedEndDate = courseEndDate.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
        });
    }

    return (
        <>
            <Wrapper>
                <Header
                    title={courseSchedule.course.title}
                    logoS3Filepath={courseSchedule.course.organisation.logoS3Filepath}
                    aboveTitleElement={
                        <NavBackButton
                            backButtonTitle={backButtonDetails.title}
                            backButtonAria={backButtonDetails.aria}
                            backButtonHref={backButtonDetails.href}
                            backButtonNavigateState={backButtonDetails.navigateState}
                        />
                    }
                />

                <MainContent>
                    <DatesAndButtonsContainer id="datesAndButtonsContainer">
                        <ShareAndCancelCourse
                            courseSchedule={courseSchedule}
                            numberOfLearners={courseSchedule.enrolledLearnersCount ?? 0}
                            closeEnrolLinkModalButtonRef={closeEnrolLinkModalButtonRef}
                            cancelCourseButtonRef={cancelCourseButtonRef}
                        />
                    </DatesAndButtonsContainer>
                    <DateAndTrainerCardsContainer>
                        <DateContainerCard>
                            {courseHasEnded ? (
                                <AlertContainer>
                                    <WarningIcon />
                                    <ErrorMessage $bold>
                                        {strings.scheduledCourseDetails.schedule.courseHasEnded}
                                    </ErrorMessage>
                                </AlertContainer>
                            ) : (
                                <DatesFlex>
                                    <DateAndIconContainer>
                                        <ScheduleIcon size={IconSize.LARGE} />
                                        <FlexCol>
                                            <BodyLarge>{strings.publicCoursePage.startDate}</BodyLarge>
                                            <LargeDateText $bold>{formattedStartDate}</LargeDateText>
                                        </FlexCol>
                                    </DateAndIconContainer>
                                    {courseSchedule.endDate && (
                                        <DateAndIconContainer>
                                            <ScheduleIcon size={IconSize.LARGE} />
                                            <FlexCol>
                                                <BodyLarge>{strings.publicCoursePage.endDate}</BodyLarge>
                                                <LargeDateText $bold>{formattedEndDate}</LargeDateText>
                                            </FlexCol>
                                        </DateAndIconContainer>
                                    )}
                                </DatesFlex>
                            )}
                            {userIsOrgAdmin && courseSchedule.user && (
                                <FlexCol>
                                    <BodyLarge>{strings.publicCoursePage.trainerHeadline}</BodyLarge>
                                    <LargeDateText $bold>
                                        {courseSchedule.user.firstName} {courseSchedule.user.lastName}
                                    </LargeDateText>
                                </FlexCol>
                            )}
                        </DateContainerCard>
                    </DateAndTrainerCardsContainer>

                    <TabStructureComponent tabs={tabDefinitions} initialActiveTabId={activeTabId} />
                </MainContent>
            </Wrapper>
        </>
    );
}

const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: calc(100vh - 184px);
`;

const MainContent = styled.div`
    width: 100%;
    height: 100%;
    max-width: ${sizes.appMaxWidthWithoutPadding}px;
    padding: ${sizes.spacingLg} ${sizes.edgePadding};
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingLg};
`;

const DatesAndButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingRg};
`;

const DateAndTrainerCardsContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

const AlertContainer = styled.div`
    display: flex;
    gap: ${sizes.spacingSm};
    align-items: center;
`;

const DatesFlex = styled(FlexAlignCenter)`
    gap: ${sizes.spacingRg};
    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        gap: ${sizes.spacingMd};
        align-items: unset;
    }
`;

const DateContainerCard = styled(LearnerCardBase)`
    padding: ${sizes.spacingMd};
    height: fit-content;
    display: flex;
    justify-content: space-between;
    gap: ${sizes.spacingXl};
    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        gap: ${sizes.spacingMd};
        width: 100%;
    }
`;

const DateAndIconContainer = styled(FlexAlignCenter)`
    gap: ${sizes.spacingMd};
    @media (max-width: ${breakpoints.sm}) {
        gap: ${sizes.spacingSm};
    }
`;

const LargeDateText = styled(BodyLarge)`
    font-size: ${FONTSIZE.Headline5};
    overflow-wrap: unset;
    white-space: nowrap;
    word-break: keep-all;
`;
